'use client'

import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react'
import { useSupabase } from '@/hooks/useSupabase'

type NotificationsState = {
  unreadCount: number
  recentMessages: any[]
  loadingMessages: boolean
  refresh: () => Promise<void>
}

const Ctx = createContext<NotificationsState | null>(null)

export function NotificationsProvider({
  children,
  enabled = true,
  intervalMs = 30000,
}: {
  children: React.ReactNode
  enabled?: boolean
  intervalMs?: number
}) {
  const { supabase, user, loading: authLoading } = useSupabase()

  const [unreadCount, setUnreadCount] = useState(0)
  const [recentMessages, setRecentMessages] = useState<any[]>([])
  const [loadingMessages, setLoadingMessages] = useState(false)

  const refresh = useCallback(async () => {
    if (!enabled || !supabase || authLoading || !user) return
    setLoadingMessages(true)
    try {
      // TODO: 기존 useNotifications 로직을 여기로 옮기거나,
      // supabase RPC / view 기반으로 unread/recent 조회를 이 자리에서 수행
      // 예: const { data } = await supabase.from('v_notifications').select('*')...
      // 아래는 placeholder
      setUnreadCount((v) => v) 
      setRecentMessages((v) => v)
    } finally {
      setLoadingMessages(false)
    }
  }, [enabled, supabase, authLoading, user])

  useEffect(() => {
    if (!enabled) return
    if (!supabase || authLoading) return

    // 로그인 전이면 초기화
    if (!user) {
      setUnreadCount(0)
      setRecentMessages([])
      return
    }

    let cancelled = false

    const run = async () => {
      try {
        await refresh()
      } catch {
        if (!cancelled) {
          // noop
        }
      }
    }

    run()

    const onProfileSwitched = () => refresh()
    window.addEventListener('profileSwitched', onProfileSwitched)

    const t = window.setInterval(() => refresh(), intervalMs)

    return () => {
      cancelled = true
      window.removeEventListener('profileSwitched', onProfileSwitched)
      window.clearInterval(t)
    }
  }, [enabled, supabase, user, authLoading, refresh, intervalMs])

  const value = useMemo(
    () => ({ unreadCount, recentMessages, loadingMessages, refresh }),
    [unreadCount, recentMessages, loadingMessages, refresh]
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useNotificationsContext() {
  const v = useContext(Ctx)
  if (!v) throw new Error('useNotificationsContext must be used within NotificationsProvider')
  return v
}
